import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Users, UserCheck, UserX, DollarSign, TrendingUp, 
  MapPin, Activity, Crown, RefreshCw, LogOut, Shield, FileText 
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { adminAPI } from '../services/api';
import { UsersModal, ActiveJobsModal, OnlineDriversModal, CompletedJobsModal, VerificationsModal } from '../components/AdminModals';
import DriversMap from '../components/DriversMap';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Modal states
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [showActiveJobsModal, setShowActiveJobsModal] = useState(false);
  const [showOnlineDriversModal, setShowOnlineDriversModal] = useState(false);
  const [showCompletedJobsModal, setShowCompletedJobsModal] = useState(false);
  const [showVerificationsModal, setShowVerificationsModal] = useState(false);

  // Data for modals
  const [allUsers, setAllUsers] = useState([]);
  const [activeJobs, setActiveJobs] = useState([]);
  const [onlineDrivers, setOnlineDrivers] = useState([]);
  const [completedJobs, setCompletedJobs] = useState([]);
  const [jobsSummary, setJobsSummary] = useState({});
  const [pendingVerifications, setPendingVerifications] = useState([]);

  useEffect(() => {
    fetchOverview();
    // Refresh every 30 seconds
    const interval = setInterval(fetchOverview, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchOverview = async () => {
    try {
      const data = await adminAPI.getOverview();
      setOverview(data);
      setOnlineDrivers(data.drivers?.locations || []);
      const verificationsData = await adminAPI.getVerifications('pending');
      setPendingVerifications(verificationsData.drivers || []);
    } catch (error) {
      console.error('Error fetching overview:', error);
      toast({
        title: 'Error',
        description: 'Failed to load admin data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const data = await adminAPI.getAllUsers();
      setAllUsers(data.users || []);
      setShowUsersModal(true);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load users',
        variant: 'destructive'
      });
    }
  };

  const fetchActiveJobs = async () => {
    try {
      const data = await adminAPI.getActiveJobs();
      setActiveJobs(data.active_jobs || []);
      setShowActiveJobsModal(true);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load active jobs',
        variant: 'destructive'
      });
    }
  };

  const openOnlineDriversModal = () => {
    setOnlineDrivers(overview?.drivers?.locations || []);
    setShowOnlineDriversModal(true);
  };

  const fetchCompletedJobs = async () => {
    try {
      const data = await adminAPI.getCompletedJobs();
      setCompletedJobs(data.completed_jobs || []);
      setJobsSummary(data.summary || {});
      setShowCompletedJobsModal(true);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load completed jobs',
        variant: 'destructive'
      });
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(to bottom, #0F1D2B, #17283A)' }}>
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-wheely-orange-light animate-spin mx-auto mb-4" />
          <p className="text-white">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(to bottom, #0F1D2B, #17283A)' }}>
      {/* Header */}
      <div className="border-b" style={{ background: 'linear-gradient(to right, #17283A, #0F1D2B)', borderColor: 'rgba(225, 116, 49, 0.3)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
              <div className="p-2 rounded-xl" style={{ background: 'linear-gradient(to bottom right, #E17431, #EB9868)' }}>
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Wheely <span className="hidden sm:inline">Admin</span></h1>
                <p className="text-xs text-wheely-orange-light hidden sm:block">Master Overview</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={fetchOverview}
                disabled={loading}
                style={{ background: 'rgba(225, 116, 49, 0.2)', color: '#E17431' }}
                className="hover:bg-wheely-orange/30"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/admin/content')}
                style={{ borderColor: 'rgba(139, 92, 246, 0.5)', color: '#8b5cf6' }}
                className="hover:bg-purple-500/10"
              >
                <FileText className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Edit Content</span>
              </Button>
              <Button
                variant="outline"
                onClick={handleLogout}
                style={{ borderColor: 'rgba(235, 152, 104, 0.5)', color: '#EB9868' }}
                className="hover:bg-wheely-orange/10"
              >
                <LogOut className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Key Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card 
            onClick={openOnlineDriversModal}
            style={{ background: 'linear-gradient(to bottom right, #10b981, #059669)', borderColor: 'rgba(16, 185, 129, 0.3)' }}
            className="cursor-pointer hover:scale-105 transition-transform duration-200"
          >
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2 text-lg">
                <UserCheck className="w-5 h-5" />
                Drivers Online
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-5xl font-bold text-white">{overview?.drivers?.online || 0}</p>
              <p className="text-green-100 text-sm mt-1">
                {overview?.drivers?.offline || 0} offline • {overview?.drivers?.total || 0} total
              </p>
              <p className="text-green-200 text-xs mt-2 font-semibold">Click to view details</p>
            </CardContent>
          </Card>

          <Card 
            onClick={fetchAllUsers}
            style={{ background: 'linear-gradient(to bottom right, #E17431, #2EB2D5)', borderColor: 'rgba(225, 116, 49, 0.3)' }}
            className="cursor-pointer hover:scale-105 transition-transform duration-200"
          >
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2 text-lg">
                <Users className="w-5 h-5" />
                Registered Users
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-5xl font-bold text-white">{(overview?.customers?.total || 0) + (overview?.drivers?.total || 0)}</p>
              <p className="text-cyan-100 text-sm mt-1">All customers & drivers</p>
              <p className="text-orange-300 text-xs mt-2 font-semibold">Click to view & edit</p>
            </CardContent>
          </Card>

          <Card 
            onClick={fetchActiveJobs}
            style={{ background: 'linear-gradient(to bottom right, #E17431, #EB9868)', borderColor: 'rgba(235, 152, 104, 0.3)' }}
            className="cursor-pointer hover:scale-105 transition-transform duration-200"
          >
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2 text-lg">
                <Activity className="w-5 h-5" />
                Active Jobs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-5xl font-bold text-white">{overview?.service_requests?.active || 0}</p>
              <p className="text-orange-100 text-sm mt-1">
                {overview?.service_requests?.completed || 0} completed
              </p>
              <p className="text-orange-200 text-xs mt-2 font-semibold">Click to view details</p>
            </CardContent>
          </Card>

          <Card 
            onClick={fetchCompletedJobs}
            style={{ background: 'linear-gradient(to bottom right, #8b5cf6, #7c3aed)', borderColor: 'rgba(139, 92, 246, 0.3)' }}
            className="cursor-pointer hover:scale-105 transition-transform duration-200"
          >
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2 text-lg">
                <DollarSign className="w-5 h-5" />
                Revenue (30d)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-5xl font-bold text-white">£{overview?.revenue?.total_last_30_days?.toFixed(0) || 0}</p>
              <p className="text-purple-100 text-sm mt-1">
                £{overview?.revenue?.commission_earned?.toFixed(2) || 0} commission
              </p>
              <p className="text-purple-200 text-xs mt-2 font-semibold">Click to view completed jobs</p>
            </CardContent>
          </Card>
        </div>

        {pendingVerifications.length > 0 && (
          <Card
            onClick={() => setShowVerificationsModal(true)}
            className="mb-8 cursor-pointer hover:scale-[1.01] transition-transform duration-200 border-0"
            style={{ background: 'linear-gradient(to right, #f59e0b, #E17431)' }}
          >
            <CardContent className="py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Shield className="w-6 h-6 text-white" />
                <div>
                  <p className="text-white font-semibold">
                    {pendingVerifications.length} driver{pendingVerifications.length > 1 ? 's' : ''} awaiting verification
                  </p>
                  <p className="text-orange-100 text-sm">Review license & insurance documents</p>
                </div>
              </div>
              <span className="text-white text-sm font-semibold">Review →</span>
            </CardContent>
          </Card>
        )}

        {/* Revenue Breakdown */}
        <Card className="mb-8" style={{ background: 'rgba(30, 58, 74, 0.8)', borderColor: 'rgba(225, 116, 49, 0.3)' }}>
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-wheely-orange-light" />
              Revenue Breakdown (Last 30 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-6">
              <div>
                <p className="text-gray-400 text-sm">Total Revenue</p>
                <p className="text-white text-2xl font-bold">
                  £{overview?.revenue?.total_last_30_days?.toFixed(2) || '0.00'}
                </p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Wheely Rescue Commission</p>
                <p className="text-green-400 text-2xl font-bold">
                  £{overview?.revenue?.commission_earned?.toFixed(2) || '0.00'}
                </p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Driver Earnings</p>
                <p className="text-wheely-orange-light text-2xl font-bold">
                  £{overview?.revenue?.driver_earnings?.toFixed(2) || '0.00'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Membership Stats */}
        <Card className="mb-8" style={{ background: 'rgba(30, 58, 74, 0.8)', borderColor: 'rgba(225, 116, 49, 0.3)' }}>
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Crown className="w-5 h-5 text-wheely-orange-light" />
              Driver Memberships
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-gray-400 text-sm">Bronze</p>
                <p className="text-wheely-orange-light text-3xl font-bold">{overview?.memberships?.bronze || 0}</p>
              </div>
              <div className="text-center">
                <p className="text-gray-400 text-sm">Silver</p>
                <p className="text-gray-300 text-3xl font-bold">{overview?.memberships?.silver || 0}</p>
              </div>
              <div className="text-center">
                <p className="text-gray-400 text-sm">Gold</p>
                <p className="text-yellow-400 text-3xl font-bold">{overview?.memberships?.gold || 0}</p>
              </div>
              <div className="text-center">
                <p className="text-gray-400 text-sm">No Membership</p>
                <p className="text-red-400 text-3xl font-bold">{overview?.memberships?.none || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Online Drivers Map */}
        <Card className="mb-8" style={{ background: 'rgba(30, 58, 74, 0.8)', borderColor: 'rgba(225, 116, 49, 0.3)' }}>
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <MapPin className="w-5 h-5 text-green-400" />
              Online Drivers ({overview?.drivers?.online || 0})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {overview?.drivers?.locations?.length > 0 && (
              <DriversMap drivers={overview.drivers.locations} />
            )}
            {overview?.drivers?.locations?.length > 0 ? (
              <div className="space-y-3 mt-4">
                {overview.drivers.locations.map((driver) => (
                  <div key={driver.id} className="flex items-center justify-between p-3 rounded-lg" style={{ background: 'rgba(15, 23, 42, 0.8)' }}>
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                      <div>
                        <p className="text-white font-semibold">{driver.name}</p>
                        <div className="flex items-center gap-2 text-sm text-gray-400">
                          <MapPin className="w-3 h-3" />
                          <span>{driver.location}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Badge style={{
                        background: driver.membership_tier === 'gold' ? 'rgba(251, 191, 36, 0.2)' :
                                   driver.membership_tier === 'silver' ? 'rgba(209, 213, 219, 0.2)' :
                                   'rgba(235, 152, 104, 0.2)',
                        color: driver.membership_tier === 'gold' ? '#fbbf24' :
                               driver.membership_tier === 'silver' ? '#d1d5db' : '#EB9868'
                      }}>
                        {driver.membership_tier}
                      </Badge>
                      <div className="text-right">
                        <p className="text-white text-sm">{driver.total_jobs} jobs</p>
                        <p className="text-wheely-orange-light text-xs">⭐ {driver.rating.toFixed(1)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <UserX className="w-12 h-12 text-gray-500 mx-auto mb-3" />
                <p className="text-gray-400">No drivers currently online</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card style={{ background: 'rgba(30, 58, 74, 0.8)', borderColor: 'rgba(225, 116, 49, 0.3)' }}>
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Activity className="w-5 h-5 text-wheely-orange-light" />
              Recent Completed Jobs
            </CardTitle>
          </CardHeader>
          <CardContent>
            {overview?.recent_activity?.length > 0 ? (
              <div className="space-y-3">
                {overview.recent_activity.map((activity) => (
                  <div key={activity.id} className="flex items-center justify-between p-3 rounded-lg" style={{ background: 'rgba(15, 23, 42, 0.8)' }}>
                    <div>
                      <p className="text-white font-semibold">{activity.service_type}</p>
                      <p className="text-sm text-gray-400">
                        {activity.customer_name} → {activity.driver_name}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-green-400 font-bold">£{activity.price?.toFixed(2)}</p>
                      <p className="text-xs text-gray-400">
                        {activity.completed_at ? new Date(activity.completed_at).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-400">No recent activity</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Modals */}
      <UsersModal
        isOpen={showUsersModal}
        onClose={() => setShowUsersModal(false)}
        users={allUsers}
        onRefresh={(newUsers) => {
          if (newUsers) setAllUsers(newUsers);
          else fetchAllUsers();
        }}
      />
      
      <ActiveJobsModal
        isOpen={showActiveJobsModal}
        onClose={() => setShowActiveJobsModal(false)}
        jobs={activeJobs}
        onRefresh={fetchActiveJobs}
      />
      
      <OnlineDriversModal
        isOpen={showOnlineDriversModal}
        onClose={() => setShowOnlineDriversModal(false)}
        drivers={onlineDrivers}
      />
      
      <CompletedJobsModal
        isOpen={showCompletedJobsModal}
        onClose={() => setShowCompletedJobsModal(false)}
        jobs={completedJobs}
        summary={jobsSummary}
      />

      <VerificationsModal
        isOpen={showVerificationsModal}
        onClose={() => setShowVerificationsModal(false)}
        drivers={pendingVerifications}
        onRefresh={fetchOverview}
      />
    </div>
  );
};

export default AdminDashboard;
